import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Clock, MapPin, CheckCircle2 } from 'lucide-react';
import { SeoPageLayout, Breadcrumbs, CallCtas } from './SeoPageLayout';
import { useSeo } from '../hooks/useSeo';
import { areaPages, routePages, formatInr, SITE_URL, PHONE_TEL } from '../data/seoPages';

const AreaPageContent = ({ page }) => {
  const title = `Airport Taxi from ${page.name} – Bangalore Airport Cab ₹1350 | SG Cabs`;
  const description = `Book an airport taxi from ${page.name} to Kempegowda International Airport (and back) at a fixed fare: Sedan ₹1350, SUV ₹1850. 24/7 pickups, flight tracking, professional drivers. Call ${PHONE_TEL}.`;
  const path = `/${page.slug}`;

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: `Airport Taxi from ${page.name}`,
        serviceType: 'Airport shuttle / taxi service',
        provider: { '@id': `${SITE_URL}/#business` },
        areaServed: { '@type': 'Place', name: `${page.name}, Bengaluru` },
        url: `${SITE_URL}${path}`,
        offers: [
          { '@type': 'Offer', name: 'Airport transfer – Sedan', price: '1350', priceCurrency: 'INR', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Airport transfer – SUV', price: '1850', priceCurrency: 'INR', availability: 'https://schema.org/InStock' },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/#service-areas` },
          { '@type': 'ListItem', position: 3, name: `Airport Taxi ${page.name}`, item: `${SITE_URL}${path}` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: `How much is a taxi from ${page.name} to Bangalore airport?`, acceptedAnswer: { '@type': 'Answer', text: `SG Cabs charges a fixed ₹1350 for a Sedan and ₹1850 for an SUV from ${page.name} to Kempegowda International Airport, including tolls. The same fare applies for airport pickups to ${page.name}.` } },
          { '@type': 'Question', name: `How long does it take from ${page.name} to the airport?`, acceptedAnswer: { '@type': 'Answer', text: `${page.name} is in ${page.zone.label}, ${page.zone.note}. Allow ${page.zone.airportTime} depending on traffic; we recommend leaving 3 hours before a domestic departure.` } },
          { '@type': 'Question', name: 'Do you do early morning and late night airport pickups?', acceptedAnswer: { '@type': 'Answer', text: `Yes. SG Cabs operates 24/7 in ${page.name}. We track your flight and the driver waits at the arrivals pickup zone with your name.` } },
        ],
      },
    ],
  }), [page, path]);

  useSeo({ title, description, path, jsonLd });

  return (
    <SeoPageLayout>
      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid={`area-page-${page.slug}`}>
        <Breadcrumbs items={[{ label: 'Service Areas', to: '/#service-areas' }, { label: `Airport Taxi ${page.name}` }]} />

        <div className="max-w-3xl">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em] mb-5">
            Taxi near you · {page.zone.label}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-[1.1] tracking-tight">
            Airport Taxi from {page.name}
            <span className="block text-[#d4af37]">Fixed Fare · 24/7</span>
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Reliable cab from {page.name} to Kempegowda International Airport (KIA) and back, with on-time pickups,
            flight tracking and professional chauffeurs. No surge pricing — the fare you see is the fare you pay.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="lux-card rounded-2xl p-6 border-[#d4af37]/40" data-testid="area-fare-sedan">
              <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Sedan · airport drop / pickup</p>
              <p className="font-display text-3xl font-semibold text-[#d4af37]">₹1,350</p>
            </div>
            <div className="lux-card rounded-2xl p-6" data-testid="area-fare-suv">
              <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">SUV · airport drop / pickup</p>
              <p className="font-display text-3xl font-semibold text-white">₹1,850</p>
            </div>
          </div>

          <CallCtas message={`Hi SG Cabs, I need an airport taxi from ${page.name}.`} />
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-16">
          <div className="lux-card rounded-2xl p-5" data-testid="area-stat-time">
            <Clock className="w-5 h-5 text-[#d4af37] mb-3" aria-hidden="true" />
            <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-1">Typical time to KIA</p>
            <p className="text-white font-semibold">{page.zone.airportTime}</p>
          </div>
          <div className="lux-card rounded-2xl p-5">
            <Plane className="w-5 h-5 text-[#d4af37] mb-3" aria-hidden="true" />
            <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-1">Flight tracking</p>
            <p className="text-white font-semibold">Free wait on delayed arrivals</p>
          </div>
          <div className="lux-card rounded-2xl p-5">
            <MapPin className="w-5 h-5 text-[#d4af37] mb-3" aria-hidden="true" />
            <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-1">Pickup</p>
            <p className="text-white font-semibold">Anywhere in {page.name}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 mt-16">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">Cab service in {page.name}</h2>
              <p className="text-gray-400 leading-relaxed">
                {page.name} is in {page.zone.label}, {page.zone.note}. SG Cabs drivers pick up from homes, offices,
                tech parks, hotels and hospitals across {page.name} around the clock. Besides airport transfers we also
                run outstation cabs at ₹14/km, fixed one way drops to Hyderabad, Chennai, Mysuru, Goa, Tirupati and
                Coimbatore, and hourly rentals from ₹375/hr — all starting from {page.name}.
              </p>
            </section>
            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">Why {page.name} travellers choose SG Cabs</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {['Fixed fare, tolls included', 'Driver details shared before pickup', 'Clean Dzire / Etios / Ertiga / Innova', 'Child seat & extra luggage on request', 'Cash, UPI or online payment', 'Owner-operated, Bengaluru-based team'].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">Frequently asked questions</h2>
              <div className="space-y-4">
                {jsonLd['@graph'][2].mainEntity.map((q) => (
                  <div key={q.name} className="lux-card rounded-2xl p-5">
                    <h3 className="text-white font-semibold mb-2">{q.name}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{q.acceptedAnswer.text}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="lux-card rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-[#d4af37] uppercase tracking-[0.15em] mb-4">One way drops from {page.name}</h3>
              <ul className="space-y-2">
                {routePages.filter((p) => !p.reverse).map((p) => (
                  <li key={p.slug}>
                    <Link to={`/${p.slug}`} className="text-gray-300 hover:text-[#d4af37] text-sm">Bangalore to {p.city} · from {formatInr(p.sedan)}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lux-card rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-[#d4af37] uppercase tracking-[0.15em] mb-4">Nearby areas</h3>
              <ul className="flex flex-wrap gap-2" data-testid="area-nearby-links">
                {areaPages.filter((a) => a.zone === page.zone && a.slug !== page.slug).map((a) => (
                  <li key={a.slug}>
                    <Link to={`/${a.slug}`} className="px-3 py-1.5 rounded-full bg-[#121212] border border-white/10 text-gray-300 text-xs hover:border-[#d4af37]/40 hover:text-[#d4af37]">{a.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </SeoPageLayout>
  );
};

export default function AreaPage({ page }) {
  return <AreaPageContent key={page.slug} page={page} />;
}
